import { interestIncomeMirror } from "@hakili/domain-types";
import { formatMoney, formatQuantity, t } from "@hakili/i18n";
import { BANK_FS_BODY, BANK_FS_LEAD, BANK_FS_META, BANK_TEXT, BANK_TEXT_2, StatCard } from "@hakili/ui";
import { ChevronRight } from "lucide-react";
import type { ReactElement } from "react";

import type { AnalysisGradeView } from "./analyse.js";
import { D2Card, D2Chip, dayMonthTime } from "./d2-parts.js";
import { DASH, fill, headSubLineParts, initialsOf } from "./dossiers.js";
import { GradeMedal } from "./grade-medal.js";

/**
 * D2 frame 62, part a (`chef.js` `screenDecision`; `ui.js` `crumb()`, `head()`, `tiles()`; `components.css` `.crumb`, `.ini`, `.tile`,
 * `.quote`): the chef's decision page, above the policy card and below it. Everything is read from the served file; nothing here is
 * computed that the door did not send, and a figure the file does not hold is left out, never a dash or a made-up line.
 */

/**
 * What this page reads of the served file: a structural subset of `DecisionFileView` (`decisions.tsx`), declared here so this file does not import
 * the module that imports it (the boundaries check's `no-circular-modules`).
 */
export interface DecisionHeadFile {
  readonly reference: string;
  readonly clientName: string | null;
  readonly analystName: string | null;
  readonly vehicleLabel: string | null;
  readonly amountFinanced: number;
  readonly durationMonths: number;
  readonly nominalRatePct: string;
  readonly gridRatePct: string | null;
  readonly gaps: readonly { readonly code: string }[];
  /** `#772`: the head's « {n} écart », as served; the screen counts nothing, so with none served the head prints no chip. */
  readonly gapCount?: number | null;
  readonly justification: string;
  readonly submittedAt: string | null;
  readonly facts: {
    readonly bankFeeAmount: number;
    readonly hakiliFeeAmount: number;
  };
  readonly recap: { readonly instalmentAmount: number };
  readonly analysis: { readonly grade: AnalysisGradeView | null } | null;
}

/** The gap that stands on the rate: the proposal below the grid, or a letter with no row in it (D2: `gap` on « Grille tarifaire »). */
const rateGapOf = (file: DecisionHeadFile): boolean =>
  file.gaps.some((gap) => gap.code === "rateBelowGrid" || gap.code === "gridRateMissing");

/** The letter the chef reads: the one re-derived from the analyst's acts, else the run's. */
const letterOf = (file: DecisionHeadFile): string | null =>
  file.analysis?.grade?.rederived?.grade ?? file.analysis?.grade?.grade ?? null;

export function DecisionHead(props: {
  readonly file: DecisionHeadFile;
  readonly bankName?: string | null | undefined;
  /** The offer door's `facts.vehicleYear`; absent until it answers, and then the label stands alone. */
  readonly vehicleYear?: number | null | undefined;
  /** The FILE door's `durationMonths`: the term the client asked, never the offer's (the Durée tile). */
  readonly requestedMonths?: number | null | undefined;
  readonly onBack: () => void;
}): ReactElement {
  const { file } = props;
  const grade = file.analysis?.grade ?? null;
  const letter = letterOf(file);
  const gapCount = file.gapCount ?? null;
  // The mean is the run's, printed only beside the letter it belongs to (the same rule as the dossier head's medal).
  const mean = grade !== null && grade.grade !== null && grade.grade === letter ? grade.average : null;
  const subLine = [
    ...headSubLineParts({
      vehicleLabel: file.vehicleLabel,
      vehicleYear: props.vehicleYear ?? null,
      amountFinanced: file.amountFinanced,
      requestedMonths: props.requestedMonths ?? null,
      bankName: props.bankName ?? null,
    }),
    fill("bank.decision.soumisePar", { analyste: file.analystName ?? DASH }),
  ].join(" · ");
  return (
    <>
      {/* D2 `.crumb`: 14 px (the body size) in the muted ink, the reference in the page's ink at 600. */}
      <div
        className={`mb-3 flex items-center gap-2 ${BANK_FS_BODY} ${BANK_TEXT_2}`}
        data-testid="bank-decision-fil"
      >
        <button
          type="button"
          onClick={props.onBack}
          className="hover:text-[color:var(--hk-brand-strong)]"
          data-testid="bank-decision-retour"
        >
          {t("bank.decision.fileRetour")}
        </button>
        <ChevronRight className="h-3 w-3" aria-hidden="true" />
        <span className={`font-semibold ${BANK_TEXT}`}>{file.reference}</span>
      </div>
      <div className="mb-[18px] mt-[14px] flex items-center gap-4" data-testid="bank-dossier-tete">
        <span
          aria-hidden="true"
          data-testid="bank-dossier-initiales"
          className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-[14px] bg-[color:var(--hk-text)] text-lg font-semibold text-white"
        >
          {initialsOf(file.clientName ?? DASH)}
        </span>
        <div className="min-w-0">
          <h1
            className="m-0 text-[length:var(--hk-fs-h1)] font-semibold leading-[var(--hk-lh-tight)] tracking-[-.01em]"
            data-testid="bank-decision-titre"
          >
            {fill("bank.decision.titre", {
              type: t("bank.decisions.typeOffre"),
              client: file.clientName ?? DASH,
            })}
          </h1>
          <p
            className="mt-[3px] text-[length:var(--hk-fs-read)] text-[color:var(--hk-text-2)]"
            data-testid="bank-decision-sous-titre"
          >
            {subLine}
          </p>
        </div>
        {gapCount === null ? null : (
          <D2Chip tone={gapCount > 0 ? "warn" : "ok"} testId="bank-decision-ecarts-puce">
            {gapCount === 0
              ? t("bank.decision.sansEcart")
              : fill("bank.decision.puceEcart", { n: String(gapCount) })}
          </D2Chip>
        )}
        <span className="flex-1" />
        <GradeMedal
          letter={letter}
          mean={mean}
          provisional={grade?.provisional === true}
          incomplete={(grade?.incomplete ?? null) !== null}
        />
      </div>
    </>
  );
}

/** D2 `.tile .s`: the sub-line is metadata, 13 px, one line. */
const Sub = (props: { readonly children: string }): ReactElement => (
  <span className="block text-[length:var(--hk-fs-meta)]" data-meta>
    {props.children}
  </span>
);

export function DecisionTiles(props: {
  readonly file: DecisionHeadFile;
  readonly borrowerInsuranceRatePct: number;
}): ReactElement {
  const { file } = props;
  const settings = { borrowerInsuranceRatePct: props.borrowerInsuranceRatePct };
  const proposed = Number(file.nominalRatePct);
  const income = interestIncomeMirror(file.amountFinanced, file.durationMonths, proposed, settings);
  const atGrid =
    file.gridRatePct === null
      ? null
      : interestIncomeMirror(file.amountFinanced, file.durationMonths, Number(file.gridRatePct), settings);
  const hot = rateGapOf(file);
  const letter = letterOf(file);
  const pct = (value: string): string => fill("bank.decision.pct", { n: value.replace(".", ",") });
  const tiles: readonly {
    readonly id: string;
    readonly label: string;
    readonly value: string;
    readonly sub: string;
    readonly hot?: boolean;
  }[] = [
    {
      id: "montant",
      label: t("bank.decision.montant"),
      value: formatQuantity(file.amountFinanced),
      sub: t("common.monnaie.fcfa"),
    },
    {
      id: "duree",
      label: t("bank.decision.duree"),
      value: String(file.durationMonths),
      sub: t("bank.decision.tuileMois"),
    },
    {
      id: "taux",
      label: t("bank.decision.tauxPropose"),
      value: pct(file.nominalRatePct),
      sub:
        file.gridRatePct === null
          ? fill("bank.decision.tuileSansGrille", { note: letter ?? DASH })
          : fill("bank.decision.tuileGrille", { taux: pct(file.gridRatePct) }),
      ...(hot ? { hot: true } : {}),
    },
    {
      id: "mensualite",
      label: t("bank.decision.mensualiteTuile"),
      value: formatQuantity(file.recap.instalmentAmount),
      sub: t("bank.decision.assuranceIncluse"),
    },
    {
      id: "interets",
      label: t("bank.decision.tuileInterets"),
      value: income === null ? DASH : formatQuantity(income),
      sub:
        income === null || atGrid === null
          ? t("common.monnaie.fcfa")
          : fill("bank.decision.tuileALaGrille", {
              signe: income - atGrid < 0 ? "−" : "+",
              n: formatQuantity(Math.abs(income - atGrid)),
            }),
    },
  ];
  return (
    <div className="mb-4 grid grid-cols-5 gap-2.5" data-testid="bank-decision-tuiles">
      {tiles.map((tile) => (
        <div key={tile.id} data-testid={`bank-decision-tuile-${tile.id}`}>
          <StatCard
            label={tile.label}
            value={tile.value}
            hint={<Sub>{tile.sub}</Sub>}
            {...(tile.hot === true ? { tone: "hot" as const } : {})}
          />
        </div>
      ))}
    </div>
  );
}

/** D2's `.card` « Justification de l'analyste » (« signée »): the quote, her name, her role, the date and the two fees. */
export function DecisionJustification(props: { readonly file: DecisionHeadFile }): ReactElement {
  const { file } = props;
  const signed = file.justification !== "";
  return (
    <D2Card
      title={t("bank.decision.justificationTitre")}
      testId="bank-decision-justification"
      {...(signed ? { sub: t("bank.decision.signee") } : {})}
    >
      {signed ? (
        <>
          <blockquote
            className={`m-0 border-l-[3px] border-[color:var(--hk-brand)] pl-3.5 ${BANK_FS_LEAD} leading-[var(--hk-lh-read)] ${BANK_TEXT}`}
          >
            {file.justification}
          </blockquote>
          <p className={`mt-2 ${BANK_FS_META} ${BANK_TEXT_2}`} data-meta>
            {fill("bank.decision.metaJustification", {
              analyste: file.analystName ?? DASH,
              date: dayMonthTime(file.submittedAt),
              banque: formatMoney(file.facts.bankFeeAmount),
              hakili: formatMoney(file.facts.hakiliFeeAmount),
            })}
          </p>
        </>
      ) : (
        <p className={`m-0 ${BANK_FS_BODY} ${BANK_TEXT_2}`}>{t("bank.decision.justificationAbsente")}</p>
      )}
    </D2Card>
  );
}
